// Carte de la prospection terrain.
//
// Une zone par commune démarchée : sa TAILLE suit la taille de la commune, sa
// COULEUR dit combien de fois on y est passé dans l'année — vert si on y passe
// rarement, rouge si on y passe souvent. Un passage s'ajoute à la main
// (« Ajouter un passage ») ou par l'import du groupe WhatsApp.
//
// Fond de carte : Géoplateforme IGN (data.geopf.fr), établissement public
// français. Les tuiles sont le SEUL appel sortant de cet écran, et il ne
// transporte aucune donnée de l'entreprise : seulement la zone regardée.
// Le référentiel des communes et les tournées viennent de notre base.

import { couleurFrequence, PALIERS_FREQUENCE, rayonZoneMetres } from '@imp/shared/zone-prospection';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
// Types seuls : aucun code Leaflet n'est chargé au démarrage de l'application.
import type { LayerGroup, Map as CarteLeaflet } from 'leaflet';
import { Plus, Trash2, Upload } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useAuth } from '@/modules/auth/AuthContext';
import {
  ajouterPassage,
  croiser,
  importerExportWhatsApp,
  libelleCommune,
  listCommunes,
  listPassages,
  supprimerPassage,
  trouverCommune,
} from '@/modules/prospection/api';

const EUR = new Intl.NumberFormat('fr-FR');

function aujourdhui(): string {
  const d = new Date();
  return `${String(d.getFullYear())}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function dateFr(iso: string): string {
  return new Date(`${iso}T12:00:00`).toLocaleDateString('fr-FR');
}

export function ProspectionPage() {
  const queryClient = useQueryClient();
  const conteneur = useRef<HTMLDivElement>(null);
  const carte = useRef<CarteLeaflet | null>(null);
  const couche = useRef<LayerGroup | null>(null);
  const fichierRef = useRef<HTMLInputElement>(null);
  const [statut, setStatut] = useState<string | null>(null);
  const { profil } = useAuth();
  // Le code de la commune choisie, et non une copie : après un ajout, le
  // panneau montre les chiffres à jour.
  const [selectionCode, setSelectionCode] = useState<string | null>(null);
  const [saisieOuverte, setSaisieOuverte] = useState(false);
  const [saisie, setSaisie] = useState({ date: aujourdhui(), commune: '', flyers: '', auteur: '' });
  const [erreurSaisie, setErreurSaisie] = useState<string | null>(null);
  // La carte se charge en réseau pendant que les données peuvent arriver du
  // cache : sans ce drapeau, le dessin des pastilles s'exécuterait une fois
  // avant que la carte existe, abandonnerait, et ne se relancerait jamais.
  const [cartePrete, setCartePrete] = useState(false);

  const communesQ = useQuery({ queryKey: ['prospection-communes'], queryFn: listCommunes });
  const passagesQ = useQuery({ queryKey: ['prospection-passages'], queryFn: listPassages });

  const communes = useMemo(
    () => croiser(communesQ.data ?? [], passagesQ.data ?? [], Date.now()),
    [communesQ.data, passagesQ.data],
  );
  // Seules les communes déjà démarchées portent une pastille : afficher les
  // 700 communes des quatre départements noierait l'information.
  const visibles = useMemo(() => communes.filter((c) => c.passages > 0), [communes]);
  const selection = communes.find((c) => c.code === selectionCode) ?? null;
  const passagesSelection = (passagesQ.data ?? [])
    .filter((p) => p.commune_code === selectionCode)
    .sort((x, y) => y.date_passage.localeCompare(x.date_passage));

  const ajoutMut = useMutation({
    mutationFn: ajouterPassage,
    onSuccess: (_r, p) => {
      setSaisie((s) => ({ ...s, commune: '', flyers: '' }));
      setSaisieOuverte(false);
      setSelectionCode(p.commune.code);
      carte.current?.setView([p.commune.latitude, p.commune.longitude], 12);
      setStatut(`✅ ${EUR.format(p.flyers)} flyers à ${p.commune.nom} le ${dateFr(p.date)}.`);
      void queryClient.invalidateQueries({ queryKey: ['prospection-passages'] });
    },
    onError: (e: unknown) => {
      setErreurSaisie(e instanceof Error ? e.message : String(e));
    },
  });
  const suppressionMut = useMutation({
    mutationFn: supprimerPassage,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['prospection-passages'] });
    },
  });

  const enregistrerSaisie = () => {
    const commune = trouverCommune(communesQ.data ?? [], saisie.commune);
    const flyers = Number(saisie.flyers.replace(/\s/g, ''));
    if ((communesQ.data ?? []).length === 0) {
      setErreurSaisie(
        'La liste des communes est vide sur le serveur : elle doit être chargée une fois (RUNBOOK §27).',
      );
    } else if (commune === null) {
      setErreurSaisie('Commune introuvable : choisissez-la dans la liste qui s’affiche en tapant.');
    } else if (!Number.isInteger(flyers) || flyers <= 0) {
      setErreurSaisie('Indiquez un nombre de flyers (ex. 300).');
    } else if (saisie.date === '' || saisie.date > aujourdhui()) {
      setErreurSaisie('La date ne peut pas être dans le futur.');
    } else {
      setErreurSaisie(null);
      const auteur =
        saisie.auteur.trim() ||
        [profil?.prenom, profil?.nom].filter(Boolean).join(' ').trim() ||
        'Saisie CRM';
      ajoutMut.mutate({ date: saisie.date, commune, flyers, auteur });
    }
  };

  const importMut = useMutation({
    mutationFn: importerExportWhatsApp,
    onSuccess: (r) => {
      setStatut(
        `✅ ${String(r.repris)} tournées reprises — ${String(r.situes)} situées sur une commune` +
          (r.aSituer > 0 ? `, ${String(r.aSituer)} à situer à la main.` : '.'),
      );
      void queryClient.invalidateQueries({ queryKey: ['prospection-passages'] });
    },
    onError: (e: unknown) => {
      setStatut(`Erreur d'import : ${e instanceof Error ? e.message : String(e)}`);
    },
  });

  // Leaflet est chargé À LA DEMANDE : il ne pèse pas sur le démarrage de
  // l'application pour ceux qui n'ouvrent jamais cette carte.
  useEffect(() => {
    void (async () => {
      const L = await import('leaflet');
      await import('leaflet/dist/leaflet.css');
      const noeud = conteneur.current;
      // Le chargement de Leaflet est asynchrone : l'écran peut avoir été quitté
      // entre-temps. `isConnected` le dit — un drapeau de closure, lui, serait
      // une constante que le compilateur sait résoudre.
      if (!noeud?.isConnected) return;
      // Leaflet marque le conteneur qu'il occupe. En mode strict, React joue
      // l'effet deux fois : sans cette garde, la seconde création échouerait
      // sur « Map container is already initialized ».
      if ((noeud as HTMLDivElement & { _leaflet_id?: number })._leaflet_id !== undefined) return;
      const m = L.map(noeud, { attributionControl: true }).setView(
        [43.92, 5.78], // Forcalquier — le secteur tient dans un écran autour
        9,
      );
      L.tileLayer(
        'https://data.geopf.fr/wmts?SERVICE=WMTS&VERSION=1.0.0&REQUEST=GetTile' +
          '&LAYER=GEOGRAPHICALGRIDSYSTEMS.PLANIGNV2&STYLE=normal&TILEMATRIXSET=PM' +
          '&FORMAT=image/png&TILEMATRIX={z}&TILEROW={y}&TILECOL={x}',
        { attribution: '© IGN — Géoplateforme', maxZoom: 18 },
      ).addTo(m);
      carte.current = m;
      setCartePrete(true);
    })();
    return () => {
      setCartePrete(false);
      // Libère le conteneur : sans cela, revenir sur l'écran retrouverait un
      // nœud déjà occupé et la carte resterait vide.
      carte.current?.remove();
      carte.current = null;
      couche.current = null;
    };
  }, []);

  // Redessine les pastilles à chaque changement de données.
  useEffect(() => {
    void (async () => {
      const m = carte.current;
      if (!m || visibles.length === 0) return;
      const L = await import('leaflet');
      if (couche.current) m.removeLayer(couche.current);
      const groupe = L.layerGroup();
      // Les plus grandes zones d'abord : une petite commune voisine reste
      // cliquable par-dessus.
      const parTaille = [...visibles].sort(
        (a, b) => rayonZoneMetres(b.population) - rayonZoneMetres(a.population),
      );
      for (const c of parTaille) {
        const couleur = couleurFrequence(c.passages12Mois);
        L.circle([c.latitude, c.longitude], {
          radius: rayonZoneMetres(c.population),
          color: couleur,
          fillColor: couleur,
          fillOpacity: 0.35,
          weight: 2,
        })
          .bindTooltip(
            `<strong>${c.nom}</strong><br>${String(c.passages12Mois)} passage(s) sur 12 mois · ${EUR.format(c.flyers)} flyers en tout` +
              (c.dernierPassage ? `<br>dernier : ${dateFr(c.dernierPassage)}` : ''),
          )
          .on('click', () => {
            setSelectionCode(c.code);
          })
          .addTo(groupe);
      }
      groupe.addTo(m);
      couche.current = groupe;
    })();
  }, [visibles, cartePrete]);

  const total = visibles.reduce((t, c) => t + c.flyers, 0);
  const aSituer = (passagesQ.data ?? []).filter((p) => !p.commune_code).length;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Prospection terrain</h1>
          <p className="text-muted-foreground">
            {visibles.length} communes démarchées · {EUR.format(total)} flyers distribués
            {aSituer > 0 ? ` · ${String(aSituer)} tournées à situer` : ''}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            size="sm"
            onClick={() => {
              setSaisieOuverte((o) => !o);
              setErreurSaisie(null);
            }}
          >
            <Plus className="h-4 w-4" /> Ajouter un passage
          </Button>
          <Button
            variant="outline"
            size="sm"
            title="Reprendre un export du groupe WhatsApp des commerciaux (_chat.txt)"
            onClick={() => fichierRef.current?.click()}
          >
            <Upload className="h-4 w-4" /> Importer l&apos;export WhatsApp
          </Button>
        </div>
        <input
          ref={fichierRef}
          type="file"
          accept=".txt"
          className="hidden"
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) importMut.mutate(f);
            e.target.value = '';
          }}
        />
      </div>

      {saisieOuverte ? (
        <Card>
          <CardContent className="pt-4">
            <form
              className="grid items-end gap-3 sm:grid-cols-[150px_1fr_120px_1fr_auto]"
              onSubmit={(e) => {
                e.preventDefault();
                enregistrerSaisie();
              }}
            >
              <div className="space-y-1.5">
                <Label htmlFor="passage-date">Date</Label>
                <Input
                  id="passage-date"
                  type="date"
                  max={aujourdhui()}
                  value={saisie.date}
                  onChange={(e) => {
                    setSaisie((s) => ({ ...s, date: e.target.value }));
                  }}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="passage-commune">Commune</Label>
                <Input
                  id="passage-commune"
                  list="passage-communes"
                  placeholder="Tapez : Mane, Manosque…"
                  autoComplete="off"
                  value={saisie.commune}
                  onChange={(e) => {
                    setSaisie((s) => ({ ...s, commune: e.target.value }));
                  }}
                />
                <datalist id="passage-communes">
                  {(communesQ.data ?? []).map((c) => (
                    <option key={c.code} value={libelleCommune(c)} />
                  ))}
                </datalist>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="passage-flyers">Flyers</Label>
                <Input
                  id="passage-flyers"
                  inputMode="numeric"
                  placeholder="300"
                  value={saisie.flyers}
                  onChange={(e) => {
                    setSaisie((s) => ({ ...s, flyers: e.target.value }));
                  }}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="passage-auteur">Qui (facultatif)</Label>
                <Input
                  id="passage-auteur"
                  placeholder={[profil?.prenom, profil?.nom].filter(Boolean).join(' ')}
                  value={saisie.auteur}
                  onChange={(e) => {
                    setSaisie((s) => ({ ...s, auteur: e.target.value }));
                  }}
                />
              </div>
              <Button type="submit" disabled={ajoutMut.isPending}>
                {ajoutMut.isPending ? 'Ajout…' : 'Ajouter'}
              </Button>
            </form>
            {erreurSaisie ? <p className="text-destructive mt-2 text-sm">{erreurSaisie}</p> : null}
          </CardContent>
        </Card>
      ) : null}

      {statut ? <p className="text-muted-foreground text-sm">{statut}</p> : null}

      {/* Légende : la couleur dit la fréquence, la taille suit la commune. */}
      <div className="text-muted-foreground flex flex-wrap items-center gap-x-4 gap-y-1 text-xs">
        <span className="font-medium">Passages sur 12 mois :</span>
        {PALIERS_FREQUENCE.map((f) => (
          <span key={f.label} className="flex items-center gap-1.5">
            <span
              className="inline-block h-3 w-3 rounded-full"
              style={{ backgroundColor: f.couleur }}
            />
            {f.label}
          </span>
        ))}
        <span className="italic">la taille de la zone suit la taille de la commune</span>
      </div>

      <div className="grid gap-4 lg:grid-cols-[1fr_320px]">
        <Card className="overflow-hidden">
          <div ref={conteneur} className="h-[60vh] w-full" />
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">
              {selection ? selection.nom : 'Communes à revoir'}
            </CardTitle>
          </CardHeader>
          <CardContent className="text-sm">
            {selection ? (
              <div className="space-y-1">
                <div>
                  <strong style={{ color: couleurFrequence(selection.passages12Mois) }}>
                    {String(selection.passages12Mois)} passage(s) sur 12 mois
                  </strong>
                </div>
                <div>
                  {EUR.format(selection.flyers)} flyers · {String(selection.passages)} passage(s) en
                  tout · {String(selection.argus)} argus
                </div>
                <ul className="mt-2 divide-y text-xs">
                  {passagesSelection.map((p) => (
                    <li key={p.id} className="flex items-center justify-between gap-2 py-1">
                      <span>
                        {dateFr(p.date_passage)} · {EUR.format(p.flyers)} flyers · {p.auteur}
                      </span>
                      {p.source === 'saisie' ? (
                        <button
                          type="button"
                          className="text-muted-foreground hover:text-destructive"
                          title="Supprimer ce passage (saisi par erreur)"
                          aria-label={`Supprimer le passage du ${dateFr(p.date_passage)}`}
                          disabled={suppressionMut.isPending}
                          onClick={() => {
                            if (
                              window.confirm(`Supprimer le passage du ${dateFr(p.date_passage)} ?`)
                            )
                              suppressionMut.mutate(p.id);
                          }}
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      ) : null}
                    </li>
                  ))}
                </ul>
                <Button
                  variant="ghost"
                  size="sm"
                  className="mt-2 px-0"
                  onClick={() => {
                    setSelectionCode(null);
                  }}
                >
                  ← Revenir à la liste
                </Button>
              </div>
            ) : (
              <>
                <p className="text-muted-foreground mb-2 text-xs">
                  Les secteurs les plus anciennement démarchés, à remettre au programme.
                </p>
                <ul className="space-y-1">
                  {[...visibles]
                    .sort((a, b) => (b.anciennete ?? 0) - (a.anciennete ?? 0))
                    .slice(0, 12)
                    .map((c) => (
                      <li key={c.code} className="flex items-center justify-between gap-2">
                        <button
                          type="button"
                          className="hover:text-primary truncate text-left"
                          onClick={() => {
                            setSelectionCode(c.code);
                          }}
                        >
                          {c.nom}
                        </button>
                        <span
                          className="shrink-0 text-xs"
                          style={{ color: couleurFrequence(c.passages12Mois) }}
                        >
                          {c.anciennete === null
                            ? '—'
                            : `${String(Math.round(c.anciennete / 30))} mois`}
                        </span>
                      </li>
                    ))}
                </ul>
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
